import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle } from 'lucide-react';
import { useTheme } from '../lib/theme';

/**
 * ConfirmDialog — glass-morphism confirm modal for destructive actions.
 *
 * Portals to `document.body` so it escapes any container clipping. Backdrop
 * click + Escape key dismiss (Cancel behaviour).
 *
 * Props:
 *   open           : boolean
 *   title          : string
 *   body           : string | ReactNode
 *   confirmLabel   : string (default 'Confirm')
 *   cancelLabel    : string (default 'Cancel')
 *   destructive    : boolean → paints the confirm button red (default true)
 *   onConfirm()    : called when the user confirms
 *   onCancel()     : called on backdrop click, Escape, or Cancel button
 */
export default function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = true,
  onConfirm,
  onCancel,
}) {
  const { colors, effective } = useTheme();
  const confirmRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onCancel?.(); };
    document.addEventListener('keydown', onKey);
    // Focus the destructive button so keyboard users see the danger.
    setTimeout(() => confirmRef.current?.focus(), 30);
    // Lock body scroll while modal is up.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onCancel]);

  if (!open) return null;

  const isDark = effective === 'dark';
  const dangerBg = '#EF4444';
  const dangerBgHover = '#DC2626';

  const modal = (
    <div
      data-testid="confirm-dialog-backdrop"
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        backgroundColor: isDark ? 'rgba(0,0,0,0.55)' : 'rgba(15,23,42,0.45)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
    >
      <div
        data-testid="confirm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        onClick={(e) => e.stopPropagation()}
        style={{
          minWidth: 320,
          maxWidth: 440,
          width: '100%',
          borderRadius: 20,
          padding: '24px 24px 20px',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)'}`,
          backgroundColor: isDark ? 'rgba(20,20,24,0.72)' : 'rgba(255,255,255,0.78)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          boxShadow: isDark
            ? '0 20px 60px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)'
            : '0 20px 60px rgba(15,23,42,0.18), inset 0 1px 0 rgba(255,255,255,0.6)',
          color: colors.text,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          {destructive ? (
            <div
              style={{
                width: 40, height: 40, borderRadius: 999,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: 'rgba(239,68,68,0.14)', flexShrink: 0,
              }}
            >
              <AlertTriangle size={20} color={dangerBg} />
            </div>
          ) : null}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2
              id="confirm-dialog-title"
              data-testid="confirm-dialog-title"
              style={{ margin: 0, fontSize: 17, fontWeight: 700, lineHeight: 1.35, color: colors.text }}
            >
              {title}
            </h2>
            {body ? (
              <p
                data-testid="confirm-dialog-body"
                style={{ margin: '6px 0 0', fontSize: 14, lineHeight: 1.5, color: colors.textMuted }}
              >
                {body}
              </p>
            ) : null}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 22,
          }}
        >
          <button
            data-testid="confirm-dialog-cancel"
            onClick={onCancel}
            style={{
              height: 40, minWidth: 88, padding: '0 16px',
              borderRadius: 10,
              border: `1px solid ${colors.border}`,
              backgroundColor: 'transparent',
              color: colors.text,
              fontSize: 14, fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            data-testid="confirm-dialog-confirm"
            onClick={onConfirm}
            onMouseEnter={(e) => { if (destructive) e.currentTarget.style.backgroundColor = dangerBgHover; }}
            onMouseLeave={(e) => { if (destructive) e.currentTarget.style.backgroundColor = dangerBg; }}
            style={{
              height: 40, minWidth: 88, padding: '0 18px',
              borderRadius: 10, border: 'none',
              backgroundColor: destructive ? dangerBg : colors.accent,
              color: '#FFFFFF',
              fontSize: 14, fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 1px 0 rgba(255,255,255,0.14) inset, 0 4px 12px rgba(239,68,68,0.24)',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
